import { useEffect, useState, useRef } from 'react';
import { NudosButton, NudosStaticAlertInComponent, NudosTextInput } from '../../../../../components/NudosComponents';
import NudosDatePicker from '../../../../../components/NudosComponents/NudosDatePicker';
import LogisticsServiceTrelloCard from '../../LogisticsServiceTrelloCard/LogisticsServiceTrelloCard';
import {
  IHistorySL,
  IactiveLsStatusDto,
  IlogData,
  IlogisticServiceDetails,
  IlogisticServiceTrelloCardData,
  LOCATION_PLACE_TYPE
} from '../../../../../types/services';
import { getNudosData } from '../../../../../utils/getNudosData';
import { editActiveServiceInformation, postReportLogisticServiceProblem } from '../../../../../services/services';
import errorMessage from '../../../../../utils/errorMessage';
import NudosReportLogisticServiceProblem from '../../../../../components/NudosReportLogisticServiceProblem/NudosReportLogisticServiceProblem';
import { formatShortDateAndHour, sortObjectWithDates } from '../../../../../utils/formatDatesAndHours';
import alertMessage from '../../../../../utils/alertMessage';
import { getLsDateLogLabel } from '../../../../LogisticServices/utils/general.utils';
import useServiceState from '../../../../../state/useServicesState';
import './DelayedModalLS.scss';

const DelayedModalLsContent = ({
  handleSuccessInUpdatingStatus,
  logisticServiceDetails,
  closeModalAfterSubmitCallback,
  selectedLogisticServiceDataForModal
}: {
  handleSuccessInUpdatingStatus: () => void;
  logisticServiceDetails?: IlogisticServiceDetails;
  closeModalAfterSubmitCallback: () => void;
  selectedLogisticServiceDataForModal?: IlogisticServiceTrelloCardData;
}) => {
  const nudosData = getNudosData();
  const { reportReason, reasonAttempt } = useServiceState();
  const reportSectionRef = useRef<HTMLDivElement>(null);

  const [trackingCode, setTrackingCode] = useState<string>();
  const [trackingLink, setTrackingLink] = useState<string>();
  const [valuesHadChanged, setValuesHadChanged] = useState<boolean>(false);
  const [deliveryDateSince, setDeliveryDateSince] = useState<string>();
  const [deliveryDateUntil, setDeliveryDateUntil] = useState<string>();
  const [collectionDateSince, setCollectionDateSince] = useState<string>();
  const [collectionDateUntil, setCollectionDateUntil] = useState<string>();
  const [deliveryProviderName, setDeliveryProviderName] = useState<string>();
  const [modalButtonIsLoading, setModalButtonIsLoading] = useState<boolean>(false);

  const completedTrackingInformation =
    !!deliveryDateSince && !!deliveryDateUntil && !!collectionDateSince && !!collectionDateUntil;
  const requireCollectionDate = [
    LOCATION_PLACE_TYPE.USER,
    LOCATION_PLACE_TYPE.OTHER,
    LOCATION_PLACE_TYPE.OFFICE
  ].includes(logisticServiceDetails?.originLocation?.place as LOCATION_PLACE_TYPE);
  const requireDeliveryDate = [
    LOCATION_PLACE_TYPE.USER,
    LOCATION_PLACE_TYPE.OTHER,
    LOCATION_PLACE_TYPE.OFFICE,
    LOCATION_PLACE_TYPE.DIAGNOSTIC,
    LOCATION_PLACE_TYPE.WARRANTY
  ].includes(logisticServiceDetails?.destinationLocation?.place as LOCATION_PLACE_TYPE);
  const requireBothDates = requireCollectionDate && requireDeliveryDate;
  const logisticServiceId = logisticServiceDetails?.id;
  const reports = logisticServiceDetails?.reports;
  const historySL = logisticServiceDetails?.historySL;
  const displayReportsHistory = reports?.find(report => report?.currentStatus === 'OPEN');
  const reportWithoutAttempt = [6, 7, 8, 9]?.includes(reportReason?.id || 0);

  const getDataForLogsWithTries = (logsWithTries: IHistorySL[]) => {
    let allLogsWithTries: IlogData[] = [];
    for (const logWithTries of logsWithTries) {
      const { tries = [], reasonGlobalStatus, reason, status } = logWithTries;
      if (!reasonGlobalStatus || !reason || !tries.length || !status) continue;
      const logWithItsTries: IlogData[] = [];
      tries.forEach(attempt => {
        if (!attempt?.date) return;
        const attemptLog = {
          date: attempt?.date,
          reasonGlobalStatus,
          reason,
          status
        };
        logWithItsTries.push(attemptLog);
      });

      allLogsWithTries = allLogsWithTries.concat(logWithItsTries);
    }
    const sortedLogsWithTries: IlogData[] = sortObjectWithDates(allLogsWithTries, 'asc');
    const formattedDates = sortedLogsWithTries.map((log, i) => {
      const attemptNumberText =
        i === 0 ? '| Primer intento' : i === 1 ? '| Segundo intento' : i === 2 ? '| Tercer intento' : '';
      const formattedDate = {
        date: log.date,
        label: getLsDateLogLabel(log.reason, log.reasonGlobalStatus, attemptNumberText, log?.status, true)
      };
      return formattedDate;
    });

    return formattedDates;
  };

  const getStatusesAndReportsDatesLogs = () => {
    if (!historySL?.length) return [];
    const logsWithTries = [];
    const formatedDates = [];
    for (const log of historySL) {
      if (!!log?.tries?.length) {
        logsWithTries.push(log);
      } else if (log?.reasonGlobalStatus === 'DELAY/ACTIVE') {
        const formattedDate = {
          date: log?.date,
          label: getLsDateLogLabel(log?.reason || log?.status, log.reasonGlobalStatus, '', log.status, true)
        };
        formatedDates.push(formattedDate);
      }
    }
    const formattedLogsWithTries = getDataForLogsWithTries(logsWithTries);
    return [...formatedDates, ...formattedLogsWithTries];
  };

  const statusesAndReportsDatesLogs = getStatusesAndReportsDatesLogs();
  const allLogisitcDatesSorted = sortObjectWithDates(statusesAndReportsDatesLogs, 'desc');

  const handleDisabledButton = () => {
    if (requireBothDates && !completedTrackingInformation) return true;
    if (requireCollectionDate && (!collectionDateSince || !collectionDateUntil)) return true;
    if (requireDeliveryDate && (!deliveryDateSince || !deliveryDateUntil)) return true;
    if (!reportReason?.id || !valuesHadChanged) return true;
    if (!reportWithoutAttempt && !reasonAttempt) return true;
    return false;
  };

  const changeLogisticServiceInformation = async (body: IactiveLsStatusDto) => {
    if (!logisticServiceId || !reportReason) return;
    setModalButtonIsLoading(true);
    try {
      const reportBody = {
        userId: nudosData?.userData?.id,
        reasonId: reportReason?.id,
        logisticServiceId: logisticServiceDetails?.id,
        numberOfTry: reasonAttempt?.value || undefined
      };
      await editActiveServiceInformation(logisticServiceId, body);
      await postReportLogisticServiceProblem(reportBody);
      handleSuccessInUpdatingStatus();
      alertMessage('Reporte del problema con este servicio enviado con éxito.');
      closeModalAfterSubmitCallback();
    } catch (error) {
      errorMessage();
    } finally {
      setModalButtonIsLoading(false);
    }
  };

  const handleSaveButton = () => {
    const currentNudosUser = getNudosData();
    if (!currentNudosUser?.userData?.id) return;
    const body = {
      collectSince: collectionDateSince,
      collectUntil: collectionDateUntil,
      deliveryUntil: deliveryDateUntil,
      deliverySince: deliveryDateSince,
      trackingCode: trackingCode,
      trackingLink: trackingLink,
      deliveryProviderName: deliveryProviderName
    };
    changeLogisticServiceInformation(body);
  };

  const estimatedDeliveryDateHasChanged =
    !!deliveryDateUntil &&
    !!deliveryDateSince &&
    !!logisticServiceDetails?.estimatedDates?.delivery?.since &&
    !!logisticServiceDetails?.estimatedDates?.delivery?.until &&
    (logisticServiceDetails?.estimatedDates?.delivery?.since !== deliveryDateSince ||
      logisticServiceDetails?.estimatedDates?.delivery?.until !== deliveryDateUntil);

  const trackingInformactionAdded =
    !!trackingCode && !!trackingLink && !logisticServiceDetails?.trackingLink && !logisticServiceDetails?.trackingCode;

  const displayEditDeliveryDateAlert = !!trackingInformactionAdded || !!estimatedDeliveryDateHasChanged;

  const editDeliveryDateAlertOptions = {
    title: trackingInformactionAdded ? (
      <div className="editDeliveryDateAlert">
        Has agregado la <strong>información de tracking.</strong> El cliente recibirá un correo con los detalles
      </div>
    ) : (
      <div className="editDeliveryDateAlert">
        Has actualizado la <strong>fecha estimada de entrega</strong>. Enviaremos un correo al cliente con los detalles
      </div>
    )
  };

  useEffect(() => {
    !trackingCode && logisticServiceDetails?.trackingCode && setTrackingCode(logisticServiceDetails?.trackingCode);
    !trackingLink && logisticServiceDetails?.trackingLink && setTrackingLink(logisticServiceDetails?.trackingLink);
    !deliveryDateSince &&
      logisticServiceDetails?.estimatedDates?.delivery?.since &&
      setDeliveryDateSince(logisticServiceDetails?.estimatedDates?.delivery?.since);
    !deliveryDateUntil &&
      logisticServiceDetails?.estimatedDates?.delivery?.until &&
      setDeliveryDateUntil(logisticServiceDetails?.estimatedDates?.delivery?.until);
    !collectionDateSince &&
      logisticServiceDetails?.estimatedDates?.collect?.since &&
      setCollectionDateSince(logisticServiceDetails?.estimatedDates?.collect?.since);
    !collectionDateUntil &&
      logisticServiceDetails?.estimatedDates?.collect?.until &&
      setCollectionDateUntil(logisticServiceDetails?.estimatedDates?.collect?.until);
    !deliveryProviderName &&
      logisticServiceDetails?.provider &&
      setDeliveryProviderName(logisticServiceDetails?.provider);
  }, [logisticServiceDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const scrollToReportSection = () => {
      setTimeout(() => {
        reportSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    };

    scrollToReportSection();
  }, []);

  return (
    <div id="delayedModalLS">
      <div className="containerDelayedModal">
        <div className="titleDelayed">Estás a punto de actualizar las fechas del siguiente servicio:</div>
        <div className="formContainer">
          {selectedLogisticServiceDataForModal && (
            <LogisticsServiceTrelloCard logisticServiceData={selectedLogisticServiceDataForModal} />
          )}
          <div className="datesContainer">
            <div className="datesTitle">Actualizar fechas</div>
            {requireCollectionDate && (
              <div className="containerLine">
                <NudosDatePicker
                  label="Fecha estimada de recolección (Desde)*"
                  isFilled={!!collectionDateSince}
                  customClassName="customInput"
                  defaultValue={collectionDateSince}
                  updateCurrentValueCallback={value => {
                    setCollectionDateSince(value);
                    setValuesHadChanged(true);
                  }}
                />
                <NudosDatePicker
                  label="Fecha estimada de recolección (Hasta)*"
                  isFilled={!!collectionDateUntil}
                  customClassName="customInput"
                  defaultValue={collectionDateUntil}
                  updateCurrentValueCallback={value => {
                    setCollectionDateUntil(value);
                    setValuesHadChanged(true);
                  }}
                />
              </div>
            )}
            {requireDeliveryDate && (
              <div className="containerLine">
                <NudosDatePicker
                  label="Fecha estimada de entrega (Desde)*"
                  isFilled={!!deliveryDateSince}
                  customClassName="customInput"
                  defaultValue={deliveryDateSince}
                  updateCurrentValueCallback={value => {
                    setDeliveryDateSince(value);
                    setValuesHadChanged(true);
                  }}
                />
                <NudosDatePicker
                  label="Fecha estimada de entrega (Hasta)*"
                  isFilled={!!deliveryDateUntil}
                  customClassName="customInput"
                  defaultValue={deliveryDateUntil}
                  updateCurrentValueCallback={value => {
                    setDeliveryDateUntil(value);
                    setValuesHadChanged(true);
                  }}
                />
              </div>
            )}
            <div className="containerLine">
              <NudosTextInput
                label="Link para tracking del servicio (opcional)"
                isFilled={!!trackingLink}
                placeholder="Pega el link de tracking"
                defaultValue={trackingLink}
                handleChange={value => {
                  setTrackingLink(value);
                  setValuesHadChanged(true);
                }}
                customClassName="customInput"
              />
              <NudosTextInput
                label="Código/guía de rastreo (opcional)"
                isFilled={!!trackingCode}
                placeholder="Escribe el código/guía aquí"
                defaultValue={trackingCode}
                handleChange={value => {
                  setTrackingCode(value);
                  setValuesHadChanged(true);
                }}
                customClassName="customInput"
              />
            </div>
            <div className="containerLine providerContainer">
              <NudosTextInput
                label="Proveedor (opcional)"
                isFilled={!!deliveryProviderName}
                placeholder="Escribe el nombre del proveedor"
                defaultValue={deliveryProviderName}
                handleChange={value => {
                  setDeliveryProviderName(value);
                  setValuesHadChanged(true);
                }}
                customClassName="customInput"
              />
            </div>

            {displayEditDeliveryDateAlert && (
              <NudosStaticAlertInComponent
                alertOptions={editDeliveryDateAlertOptions}
                color="blue"
                type="information"
              />
            )}
          </div>
          <div ref={reportSectionRef}>
            <div className="reportProblemContainer">
              <div className="reportTitle">Razones de cambio de fecha o atraso</div>
              <NudosReportLogisticServiceProblem
                customClassname="reportSelect"
                logisticServiceDetails={logisticServiceDetails}
                autoOpenDropdown
                displayReportLSProblem
                displayAttemptsSelect
              />
              {displayReportsHistory && (
                <div className="reporstHistoryContainer">
                  <div className="historyTitle">Historial de actualizaciones</div>
                  {allLogisitcDatesSorted?.map((date, index) => {
                    return (
                      date?.date && (
                        <div key={`sorted-date-${date?.date}-${index}`} className="reporstHistory">
                          {date?.label} {formatShortDateAndHour(date?.date)?.textWithTimezone}
                        </div>
                      )
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="buttonContainer">
          <NudosButton
            buttonText="Guardar"
            componentSize="large"
            handleClick={handleSaveButton}
            customClassName="saveButton"
            isButtonLoading={modalButtonIsLoading}
            isButtonDisabled={handleDisabledButton()}
          />
        </div>
      </div>
    </div>
  );
};

export default DelayedModalLsContent;
